import { z } from 'zod'
import { SectionPermissions } from './deling.js'
import { Block, DossierId, InstantieId, ToekenningId, InvitationId } from './id.js'
import { Side } from './identity.js'
import { Moment, Person, compareMoments } from './moment.js'

/**
 * Een uitnodiging op een eenmalige code (R-64, R-64c, R-65, R-67, ontwerp 4.8 en 6.4).
 *
 * Wie mag delen (R-65d) nodigt iemand uit op e-mailadres (R-64a), met per sectie lezen of
 * bewerken. De uitnodiging draagt een eenmalige code die kort geldig is. Pas als de ontvanger
 * de code verzilvert en inlogt, ontstaat er een toekenning met bron deling in het register,
 * gebonden aan de vaste sleutel van wie inlogde (R-155e); tot dan geeft de uitnodiging geen
 * toegang, en ze kan worden geweigerd (R-64c).
 *
 * **Een eigen vorm naast de toekenning, en geen toestand op de toekenning.** Zo blijft `persoon`
 * op een toekenning verplicht: een uitnodiging heeft nog geen persoon, alleen een adres. En een
 * pod van na opdracht 4b parset `Toekenning` strikt, dus een veld erbij zou hem laten omvallen
 * (D-36). Het verband tussen de twee staat alleen hier, in `toekenningId`.
 *
 * **De code zelf staat er niet in.** Alleen haar SHA-256-hash: wie de opslag leest kan er geen
 * uitnodiging mee verzilveren. De code bestaat alleen in de transactie die haar maakt en in het
 * bericht naar het processysteem, dat haar naar de ontvanger brengt (R-64b, `outbox.ts`).
 *
 * **Het e-mailadres is een extra slot, en nooit de grond.** De binding gebeurt op de code en de
 * vaste sleutel. Het adres in het token moet bij verzilveren wel gelijk zijn aan dat van de
 * uitnodiging: dat kan alleen beperken. Microsoft schrijft in de "ID token claims reference" dat
 * de claim `email` niet gegarandeerd juist is en nooit voor autorisatie mag worden gebruikt
 * (ontwerp 4.8), en daarom geeft dit adres nooit iets wat de code niet al gaf.
 */

/**
 * Zeven dagen.
 *
 * Lang genoeg voor een mail die niet meteen wordt gelezen: een uitnodiging die op vrijdag
 * vertrekt is na een week vakantie nog te gebruiken. Kort genoeg dat een vergeten link vervalt
 * voordat hij in een doorgestuurde mail of een oud postvak opnieuw opduikt. Wie te laat is, vraagt
 * de deler opnieuw uit te nodigen; dat kost een klik en geen beheerhandeling.
 */
export const INVITATION_VALID_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Een e-mailadres zoals het wordt bewaard en vergeleken: zonder witruimte en in kleine letters.
 *
 * Eén functie voor beide kanten van de vergelijking, zodat het adres van de uitnodiging en het
 * adres uit het token op dezelfde manier worden gelezen.
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

/** Een e-mailadres dat al genormaliseerd is. */
export const NormalizedEmail = z
  .email()
  .refine((email) => email === normalizeEmail(email), {
    message: 'een e-mailadres staat in kleine letters en zonder witruimte',
  })

/** De SHA-256 van de code, als 64 hextekens in kleine letters. */
export const Codehash = z.string().regex(/^[0-9a-f]{64}$/, 'verwacht een SHA-256-hash als 64 hextekens')

/**
 * De code zoals de ontvanger haar aanbiedt: 32 willekeurige bytes als base64url zonder opvulling,
 * dus 43 tekens.
 */
export const InvitationCode = z
  .string()
  .regex(/^[A-Za-z0-9_-]{43}$/, 'verwacht een uitnodigingscode van 43 tekens')

/**
 * Waar een uitnodiging staat.
 *
 * Open geeft nog geen toegang. Verzilverd betekent dat er een toekenning is; of die nog leeft
 * staat op de toekenning, want intrekken en vervallen gebeuren daar (R-66, R-65e). De andere
 * vier zijn een einde zonder toekenning: geweigerd door de ontvanger (R-64c), ingetrokken door
 * de deler (R-66), verlopen omdat de code te oud was, en vervallen omdat de deler zijn toegang
 * verloor voordat de code werd verzilverd (R-65e).
 */
export const InvitationStatus = z.enum(['open', 'verzilverd', 'geweigerd', 'ingetrokken', 'verlopen', 'vervallen'])
export type InvitationStatus = z.infer<typeof InvitationStatus>


export const Invitation = z
  .strictObject({
    id: InvitationId,
    /** Het dossier van de instantie waarop is gedeeld. */
    dossierId: DossierId,
    /** Het bouwblok van die instantie; de toekenning die eruit volgt geldt voor dit ene blok (R-155b). */
    blok: Block,
    /** De instantie waarop is gedeeld. */
    instantieId: InstantieId,
    /** Het adres waarop is uitgenodigd (R-64, R-64a), genormaliseerd. */
    email: NormalizedEmail,
    /** De hash van de eenmalige code; de code zelf wordt nooit bewaard (R-64c). */
    codeHash: Codehash,
    /** Per sectie lezen of bewerken (R-65, R-67). */
    rechten: SectionPermissions,
    /** Wie uitnodigde (R-155b). De cascade van R-65e loopt hierop. */
    deler: Person,
    /** De kant van de deler, uit zijn ingang (R-155a). Het overzicht van R-66a filtert hierop. */
    delerKant: Side,
    aangemaaktOp: Moment,
    /** Tot wanneer de code te verzilveren is. */
    geldigTot: Moment,
    toestand: InvitationStatus,
    /** Wanneer de uitnodiging uit de toestand open ging; null zolang ze open is. */
    afgehandeldOp: Moment.nullable(),
    /** Wie verzilverde: de ingang met de vaste sleutel uit het token (R-155e). Alleen bij verzilverd. */
    ontvanger: Person.nullable(),
    /** De toekenning die bij verzilveren ontstond. Alleen bij verzilverd. */
    toekenningId: ToekenningId.nullable(),
  })
  .superRefine((invitation, ctx) => {
    if (compareMoments(invitation.geldigTot, invitation.aangemaaktOp) <= 0) {
      ctx.addIssue({ code: 'custom', path: ['geldigTot'], message: 'een uitnodiging is geldig tot na haar aanmaken' })
    }
    const open = invitation.toestand === 'open'
    if (open !== (invitation.afgehandeldOp === null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['afgehandeldOp'],
        message: 'een uitnodiging draagt het moment van afhandeling precies als ze niet meer open is',
      })
    }
    if (invitation.afgehandeldOp !== null && compareMoments(invitation.afgehandeldOp, invitation.aangemaaktOp) < 0) {
      ctx.addIssue({ code: 'custom', path: ['afgehandeldOp'], message: 'een uitnodiging wordt niet afgehandeld voordat ze bestond' })
    }
    const redeemed = invitation.toestand === 'verzilverd'
    for (const field of ['ontvanger', 'toekenningId'] as const) {
      if (redeemed !== (invitation[field] !== null)) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: `een uitnodiging noemt ${field} precies als ze verzilverd is`,
        })
      }
    }
    if (invitation.ontvanger !== null && invitation.ontvanger === invitation.deler) {
      ctx.addIssue({ code: 'custom', path: ['ontvanger'], message: 'wie deelt verzilvert zijn eigen uitnodiging niet' })
    }
  })
export type Invitation = z.infer<typeof Invitation>

/**
 * Hoe een deling er voor de deelnemers uitziet (R-66).
 *
 * Eén woord voor wat de ontvanger kan: uitgenodigd zolang de code open en geldig is,
 * binnengekomen zolang de toekenning leeft, en daarna het einde dat erop volgde. Een open
 * uitnodiging waarvan de geldigheid voorbij is, heet hier verlopen, ook als niemand de code nog
 * heeft geprobeerd.
 */
export const DelingState = z.enum(['uitgenodigd', 'binnengekomen', 'geweigerd', 'ingetrokken', 'verlopen', 'vervallen'])
export type DelingState = z.infer<typeof DelingState>

/**
 * Eén regel in het delingsoverzicht (R-66, R-66a).
 *
 * Wat de deelnemers zien: het adres, de rechten per sectie, wie deelde en aan welke kant, en de
 * stand. Geen code, geen hash, geen ontvanger en geen toekenning: die zeggen de deelnemers niets
 * en de code zou het overzicht tot een sleutelbos maken.
 */
export const DelingRule = z.strictObject({
  id: InvitationId,
  instantieId: InstantieId,
  email: NormalizedEmail,
  rechten: SectionPermissions,
  deler: Person,
  delerKant: Side,
  stand: DelingState,
  aangemaaktOp: Moment,
  geldigTot: Moment,
  /** Sinds wanneer de stand geldt; null zolang er is uitgenodigd. */
  sinds: Moment.nullable(),
})
export type DelingRule = z.infer<typeof DelingRule>
